import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Video } from "lucide-react";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import Footer from "@/sections/Footer";
import { Eyebrow, MaskedLine, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    n: "01",
    title: "Consultation",
    price: "£95",
    note: "60 minutes",
    body: "Let's talk about your idea, your goals and where you are in your author journey. We'll discuss your story, target age, publishing options and your next steps.",
  },
  {
    n: "02",
    title: "Develop your book",
    price: "From £150",
    body: "Turn your idea into a clear children's book concept. Guidance with your story, characters, structure, themes and manuscript development.",
  },
  {
    n: "03",
    title: "Prepare to publish",
    price: "From £195",
    body: "Understand what you need to get your book ready for publication, including illustrations, formatting, covers, ISBNs and self-publishing options such as Amazon KDP.",
  },
  {
    n: "04",
    title: "Build your author brand",
    price: "From £195",
    body: "Position yourself as an author and start building your presence. Guidance with your author profile, website, social media, book promotion and launch ideas.",
  },
];

const PACKAGE_INCLUDES = [
  "Initial consultation",
  "Story development guidance",
  "Manuscript guidance",
  "Self-publishing roadmap",
  "Author branding guidance",
  "Marketing & launch plan",
  "Personalised action plan",
  "Follow-up support",
];

export default function AuthorJourneyPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "I'd like to book the 60-minute consultation (£95)." });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, type: "author-journey" });
      toast.success("Booked in — Damali will reply personally to arrange your consultation.");
      setForm({ name: "", email: "", message: "I'd like to book the 60-minute consultation (£95)." });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const input = "w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition";

  return (
    <div className="grain">
      <Navbar />
      <main className="pt-28 sm:pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div>
              <Eyebrow className="mb-5">Personal guidance for aspiring children's book authors</Eyebrow>
              <h1 className="font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-walnut">
                <MaskedLine i={0}>From story idea</MaskedLine>
                <MaskedLine i={1}>
                  to <em className="not-italic text-terracotta">published author</em>
                </MaskedLine>
              </h1>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="mt-7 max-w-xl font-display font-semibold text-lg sm:text-xl text-walnut leading-snug border-l-4 border-terracotta pl-5">
                  “I help aspiring children's authors understand the journey from story
                  idea to published book.”
                </p>
                <p className="mt-6 max-w-xl text-base sm:text-lg text-walnutsoft leading-relaxed">
                  Have you got a children's book idea but don't know what to do next?
                </p>
                <p className="mt-3 max-w-xl text-sm sm:text-base text-walnutsoft leading-relaxed">
                  As a published children's author, I offer practical one-to-one guidance
                  to help you navigate your journey from story idea to published book.
                </p>
                <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-sage">
                  <Video size={16} /> Online consultations
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="spotlight rounded-[2rem] border border-sand p-8 sm:p-10 overflow-hidden">
                <img
                  src="/images/damali-author.webp"
                  alt="Damali Allison, published children's author and mentor"
                  className="w-full max-w-md mx-auto aspect-[4/5] object-cover rounded-2xl shadow-[0_30px_60px_-24px_rgba(49,67,92,0.4)]"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-walnut text-cream rounded-2xl px-6 py-4 shadow-xl rotate-[-2deg]">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta mb-1">Your mentor</p>
                <p className="font-display font-bold text-lg leading-tight">Damali Allison<br />5× published author</p>
              </div>
            </motion.div>
          </div>

          <Reveal className="mb-12">
            <Eyebrow className="mb-4">The author journey</Eyebrow>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">
              Choose your step
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.07}>
                <div
                  data-testid={`journey-step-${s.n}`}
                  className="h-full bg-white/70 border border-sand rounded-3xl p-7 sm:p-8 hover:-translate-y-1.5 hover:shadow-[0_24px_44px_-20px_rgba(49,67,92,0.3)] transition-all duration-300"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <span className="font-mono font-bold text-3xl sm:text-4xl text-terracotta/70 leading-none">{s.n}</span>
                    <span className="text-right">
                      <span className="block font-display font-extrabold text-xl sm:text-2xl text-walnut">{s.price}</span>
                      {s.note && <span className="block text-xs text-walnutmuted mt-0.5">{s.note}</span>}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-walnut mb-2">{s.title}</h3>
                  <p className="text-sm sm:text-base text-walnutsoft leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mb-24">
            <div
              data-testid="journey-step-05"
              className="relative bg-walnut text-cream rounded-[2rem] p-8 sm:p-12 overflow-hidden"
            >
              <div className="absolute w-[26rem] h-[26rem] rounded-full bg-terracotta/20 blur-[110px] -top-24 -right-24 pointer-events-none" />
              <div className="grid lg:grid-cols-2 gap-10 relative z-10">
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <span className="font-mono font-bold text-3xl sm:text-4xl text-terracotta leading-none">05</span>
                    <span className="font-display font-extrabold text-3xl sm:text-4xl">£495</span>
                  </div>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-3">Author launch package</h3>
                  <p className="text-sm sm:text-base text-cream/75 leading-relaxed">
                    For those who want guidance throughout the journey — everything
                    above, brought together into one supported path from idea to launch.
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta mb-4">Includes</p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {PACKAGE_INCLUDES.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-cream/85">
                        <Check size={16} className="text-sage shrink-0 mt-0.5" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Eyebrow className="mb-4">Ready to start?</Eyebrow>
                <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut mb-5">
                  Your story could be the beginning of your author journey
                </h2>
                <p className="text-sm sm:text-base text-walnutsoft leading-relaxed max-w-lg mb-6">
                  Book your 60-minute consultation — <strong className="text-walnut">£95</strong>, held online at a
                  time that suits you. Send your details and Damali will reply personally
                  to arrange everything.
                </p>
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-sage">
                  <Video size={16} /> Online consultations
                </p>
              </div>
              <form
                data-testid="journey-booking-form"
                onSubmit={submit}
                className="bg-white/70 border border-sand rounded-3xl p-7 sm:p-8 shadow-[0_30px_60px_-28px_rgba(49,67,92,0.35)]"
              >
                <div className="flex flex-col gap-3">
                  <input
                    data-testid="journey-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className={input}
                  />
                  <input
                    data-testid="journey-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Your email address"
                    className={input}
                  />
                  <textarea
                    data-testid="journey-message-input"
                    required
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={`${input} resize-none`}
                  />
                  <button
                    data-testid="journey-submit-button"
                    type="submit"
                    disabled={sending}
                    className="group mt-1 rounded-full bg-terracotta text-cream py-4 font-display font-bold text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-terracottadark transition-colors disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Book your consultation — £95"}
                    <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </form>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
